import { AuctionResponse } from '@bonhams1793/bonhams-typescript'
import { useState } from 'react'

import { LotResponse } from '@nx/api'
import { FireFormInput } from '@nx/fire/form-input'
import { FireImage } from '@nx/fire/image'
import { FireLink } from '@nx/fire/link'
import { stripTags } from '@nx/helpers'
import { extractDataFromHTML } from '@nx/helpers'
import { getTranslate } from '@nx/translations'

import { LotImageTypes } from '@web/helpers/enums'

import { Price } from '../Price'
import {
  StyledAuctionName,
  StyledColumn,
  StyledCostCalculatorContainer,
  StyledDisclaimer,
  StyledHeading,
  StyledImageContainer,
  StyledItem,
  StyledLotContainer,
  StyledLotInfo,
  StyledLotName,
  StyledLotPrice,
  StyledPrice,
  StyledRow,
  StyledSubHeading,
  StyledTable,
  StyledTotalContainer,
  StyledTotalQuantity,
  StyledTotalText,
} from './CostCalculator.styles'
import translate from './CostCalculator.translate.json'
import {
  SHIPPING_COST,
  getBuyersPremium,
  getNumberFormat,
  getShippingInsurance,
  getTotal,
} from './CostCalculator.utils'

const CONTACT_EMAIL = 'bids.us@bonhams.com'
const MIN_HAMMER_PRICE = 0
const MAX_HAMMER_PRICE = 1_000_000_000
const { text, textHTML } = getTranslate(translate)

export function CostCalculator({ lot, auction }: CostCalculatorProps) {
  const [hammerPrice, setHammerPrice] = useState(lot.dEstimateLow)
  const [editingHammerPrice, setEditingHammerPrice] = useState(false)

  const thumbnailUrl = lot.images.find((image) =>
    image.imageuses.find(
      (imageuse) =>
        imageuse.sImageUse.includes(LotImageTypes.thumb) ||
        imageuse.sImageUse.includes(LotImageTypes.main)
    )
  )?.image_url

  return (
    <StyledCostCalculatorContainer>
      <StyledHeading $level={3} as="h2">
        {text('costCalculator.title')}
      </StyledHeading>
      <StyledSubHeading>{text('costCalculator.subTitle')}</StyledSubHeading>
      <StyledLotContainer>
        <StyledImageContainer>
          <FireImage
            src={thumbnailUrl || ''}
            fill
            alt=""
            css={`
              object-fit: contain;
            `}
          />
        </StyledImageContainer>
        <StyledLotInfo>
          <StyledLotName>
            {stripTags(extractDataFromHTML(lot.sCatalogDesc, 'LotName'))}
          </StyledLotName>
          <StyledAuctionName>{lot.iSaleNo.sSaleName}</StyledAuctionName>
          <StyledLotPrice>
            <Price
              auctionType={auction.sSaleType}
              lotStatus={lot.sLotStatus}
              lNotPublishedResults={lot.lNotPublishedResults}
              dHammerPrice={lot.dHammerPrice}
              dHammerPremium={lot.dHammerPremium}
              lExhibition={lot.lExhibition}
              lReferDept={lot.lReferDept}
              lWithoutReserve={lot.lWithoutReserve}
              dEstimateLow={lot.dEstimateLow}
              dEstimateHigh={lot.dEstimateHigh}
              sCurrencySymbol={lot.sCurrencySymbol}
              currencyISO={lot.currency?.iso_code}
              GBPLowEstimate={lot.dLotLowValue}
              GBPHighEstimate={lot.dLotHighValue}
            />
          </StyledLotPrice>
        </StyledLotInfo>
      </StyledLotContainer>
      <StyledTable aria-label={text('costCalculator.costsTable')}>
        <StyledRow>
          <StyledItem as="label" htmlFor="hammerPrice">
            {text('costCalculator.hammerPrice')}
          </StyledItem>
          <StyledPrice as="div">
            <FireFormInput
              id="hammerPrice"
              type={editingHammerPrice ? 'number' : 'text'}
              onChange={(e) => {
                const value = e.target.valueAsNumber
                if (value < MIN_HAMMER_PRICE) {
                  setHammerPrice(MIN_HAMMER_PRICE)
                } else if (value > MAX_HAMMER_PRICE) {
                  setHammerPrice(MAX_HAMMER_PRICE)
                } else {
                  setHammerPrice(isNaN(value) ? 0 : value)
                }
              }}
              value={
                editingHammerPrice ? hammerPrice : getNumberFormat(hammerPrice)
              }
              onFocus={() => setEditingHammerPrice(true)}
              onBlur={() => setEditingHammerPrice(false)}
              step="100"
              max={1_000_000_000}
              min={0}
            />
          </StyledPrice>
        </StyledRow>
        <StyledRow>
          <StyledItem>{text('costCalculator.buyersPremium')}</StyledItem>
          <StyledPrice>
            {getNumberFormat(getBuyersPremium(hammerPrice))}
          </StyledPrice>
        </StyledRow>
        <StyledRow>
          <StyledColumn>
            <StyledItem>{text('costCalculator.estimatedShipping')}</StyledItem>
            <StyledItem $level="S">
              ({text('costCalculator.toUnitedStates')})
            </StyledItem>
          </StyledColumn>
          <StyledPrice>{getNumberFormat(SHIPPING_COST)}</StyledPrice>
        </StyledRow>
        <StyledRow>
          <StyledItem>{text('costCalculator.estimatedInsurance')}</StyledItem>
          <StyledPrice>
            {getNumberFormat(getShippingInsurance(hammerPrice))}
          </StyledPrice>
        </StyledRow>
        <StyledRow>
          <StyledItem>{text('costCalculator.salesTaxPrice')}</StyledItem>
          <StyledPrice>{text('costCalculator.tbc')}</StyledPrice>
        </StyledRow>
      </StyledTable>
      <StyledTotalContainer>
        <StyledTotalText $level="L">
          {text('costCalculator.total')}
        </StyledTotalText>
        <StyledTotalQuantity $level="L" as="output">
          {text('costCalculator.totalValue', {
            totalValue: getNumberFormat(getTotal(hammerPrice)),
          })}
        </StyledTotalQuantity>
      </StyledTotalContainer>
      <StyledDisclaimer $level="XS">
        {textHTML('costCalculator.disclaimer', {
          specialRequestContact: (
            <FireLink
              key={Math.random().toString(36).slice(2)}
              href={`mailto:${CONTACT_EMAIL}`}
              target="_blank"
              forceAnchor={true}
            >
              {CONTACT_EMAIL}
            </FireLink>
          ),
        })}
      </StyledDisclaimer>
    </StyledCostCalculatorContainer>
  )
}

interface CostCalculatorProps {
  lot: LotResponse
  auction: AuctionResponse
}
